
import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Float } from "@react-three/drei";
import * as THREE from "three";

const AIGlowingSphere2 = ({ isTalking }: { isTalking: boolean }) => {
  const sphereRef = useRef<THREE.Group>(null);
  const coreRef = useRef<THREE.Mesh>(null);
  const outerLayerRef = useRef<THREE.Mesh>(null);
  const particlesRef = useRef<THREE.Group>(null);
  
  // Create morph targets for the outer layer when talking
  const outerGeometry = useMemo(() => {
    const baseGeometry = new THREE.SphereGeometry(1.25, 64, 64);
    const morphTargets = [];
    
    // Create morph targets for the sphere's vertices
    for (let i = 0; i < 3; i++) {
      const morphGeometry = baseGeometry.clone();
      const positionAttribute = morphGeometry.getAttribute('position');
      const vertices = positionAttribute.array;
      
      // Modify vertices to create different morphing effects
      for (let j = 0; j < vertices.length; j += 3) {
        // Different distortion pattern for each morph target
        const x = vertices[j];
        const y = vertices[j + 1];
        const z = vertices[j + 2];
        const distance = Math.sqrt(x * x + y * y + z * z);
        
        if (i === 0) {
          // Ripple effect
          vertices[j] += Math.sin(distance * 10) * 0.05;
          vertices[j + 1] += Math.sin(distance * 10) * 0.05;
          vertices[j + 2] += Math.sin(distance * 10) * 0.05;
        } else if (i === 1) {
          // Spiky effect
          vertices[j] += (Math.random() - 0.5) * 0.1;
          vertices[j + 1] += (Math.random() - 0.5) * 0.1;
          vertices[j + 2] += (Math.random() - 0.5) * 0.1;
        } else {
          // Wave effect
          vertices[j] += Math.sin(y * 5) * 0.05;
          vertices[j + 1] += Math.sin(z * 5) * 0.05;
          vertices[j + 2] += Math.sin(x * 5) * 0.05;
        }
      }
      
      positionAttribute.needsUpdate = true;
      morphTargets.push(morphGeometry);
    }
    
    // Add morph targets to the base geometry
    baseGeometry.morphAttributes.position = morphTargets.map(geometry => {
      return geometry.getAttribute('position');
    });
    
    return baseGeometry;
  }, []);
  
  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    
    // Base rotation
    if (sphereRef.current) {
      sphereRef.current.rotation.y = time * 0.1;
      sphereRef.current.rotation.z = Math.sin(time * 0.2) * 0.05;
    }
    
    // Core dynamics
    if (coreRef.current) {
      if (isTalking) {
        // Strong pulsing when talking
        const pulseScale = 0.75 + Math.sin(time * 12) * 0.15;
        coreRef.current.scale.set(pulseScale, pulseScale, pulseScale);
        
        // Intense glow when talking
        (coreRef.current.material as THREE.MeshStandardMaterial).emissiveIntensity = 
          5.0 + Math.sin(time * 15) * 0.8;
      } else {
        // Steady pulsing when idle
        const idlePulse = 0.7 + Math.sin(time * 1.2) * 0.08;
        coreRef.current.scale.set(idlePulse, idlePulse, idlePulse);
        
        // Calmer glow when idle
        (coreRef.current.material as THREE.MeshStandardMaterial).emissiveIntensity = 
          3.0 + Math.sin(time * 1.5) * 0.3;
      }
    }
    
    // Outer layer dynamics with morphing
    if (outerLayerRef.current) {
      // Constant rotation on multiple axes
      outerLayerRef.current.rotation.x = time * 0.2;
      outerLayerRef.current.rotation.y = time * -0.15;
      
      // Breathing effect
      const breathe = 1.0 + Math.sin(time * 0.8) * 0.03;
      outerLayerRef.current.scale.set(breathe, breathe, breathe);
      
      // Apply morph targets when talking
      if (isTalking) {
        // Animate through morph targets
        (outerLayerRef.current.morphTargetInfluences as number[])[0] = Math.sin(time * 5) * 0.5 + 0.5;
        (outerLayerRef.current.morphTargetInfluences as number[])[1] = Math.sin(time * 7 + 1) * 0.5 + 0.5;
        (outerLayerRef.current.morphTargetInfluences as number[])[2] = Math.sin(time * 9 + 2) * 0.5 + 0.5;
      } else {
        // Reset morph targets when not talking
        if (outerLayerRef.current.morphTargetInfluences) {
          outerLayerRef.current.morphTargetInfluences[0] = 0;
          outerLayerRef.current.morphTargetInfluences[1] = 0;
          outerLayerRef.current.morphTargetInfluences[2] = 0;
        }
      }
    }
    
    // Particle animations
    if (particlesRef.current) {
      particlesRef.current.children.forEach((particle, i) => {
        const p = particle as THREE.Mesh;
        
        // Orbit animation
        const orbitSpeed = 0.2 + (i * 0.05);
        const orbitRadius = 0.7 + (i % 3) * 0.15;
        const orbitOffset = i * (Math.PI / 6);
        
        p.position.x = Math.sin(time * orbitSpeed + orbitOffset) * orbitRadius;
        p.position.z = Math.cos(time * orbitSpeed + orbitOffset) * orbitRadius;
        p.position.y = Math.sin(time * orbitSpeed * 0.7 + orbitOffset) * (orbitRadius * 0.5);
        
        // Particle pulsing
        const pulseFrequency = isTalking ? 8 : 2;
        const pulseIntensity = isTalking ? 0.15 : 0.05;
        const scale = 1.0 + Math.sin(time * pulseFrequency + i) * pulseIntensity;
        p.scale.set(scale, scale, scale);
        
        // Rotation
        p.rotation.x += 0.01;
        p.rotation.y += 0.01;
      });
    }
  });

  return (
    <Float rotationIntensity={0.15} floatIntensity={0.3} speed={1.0}>
      <group ref={sphereRef}>
        {/* Outer plasma layer with morph */}
        <mesh ref={outerLayerRef} morphTargetInfluences={[0, 0, 0]}>
          <primitive object={outerGeometry} attach="geometry" />
          <meshPhysicalMaterial 
            color="#0044FF" 
            transparent={true}
            opacity={0.3}
            roughness={0.1}
            metalness={1.0}
            clearcoat={1.0}
            transmission={0.6}
            emissive="#0099FF"
            emissiveIntensity={3.0}
          />
        </mesh>
        
        {/* Secondary layer */}
        <mesh>
          <sphereGeometry args={[1.1, 48, 48]} />
          <meshPhysicalMaterial 
            color="#0055FF"
            transparent={true}
            opacity={0.5}
            roughness={0.1}
            metalness={0.9}
            clearcoat={1.0}
            emissive="#00AAFF"
            emissiveIntensity={3.5}
          />
        </mesh>
        
        {/* Dense core */}
        <mesh ref={coreRef}>
          <sphereGeometry args={[0.75, 32, 32]} />
          <meshStandardMaterial
            color="#0033FF"
            emissive="#00AAFF"
            emissiveIntensity={6.0}
            roughness={0.1}
            metalness={1.0}
          />
        </mesh>
        
        {/* Floating energy particles */}
        <group ref={particlesRef}>
          {[...Array(15)].map((_, i) => {
            return (
              <mesh key={`particle-${i}`}>
                <sphereGeometry args={[0.03 + Math.random() * 0.05, 16, 16]} />
                <meshStandardMaterial 
                  color="#00AAFF" 
                  emissive="#00DDFF"
                  emissiveIntensity={6.0}
                />
              </mesh>
            );
          })}
        </group>
        
        {/* Energy projection when talking */}
        {isTalking && (
          <>
            <mesh position={[0, 0, 0]}>
              <sphereGeometry args={[1.4, 32, 32]} />
              <meshStandardMaterial
                color="#0066FF"
                transparent={true}
                opacity={0.3}
                emissive="#00CCFF"
                emissiveIntensity={3.0}
              />
            </mesh>
            <mesh position={[0, 0, 0]}>
              <sphereGeometry args={[1.5, 24, 24]} />
              <meshStandardMaterial
                color="#00AAFF"
                transparent={true}
                opacity={0.2}
                emissive="#00FFFF"
                emissiveIntensity={2.5}
              />
            </mesh>
          </>
        )}
      </group>
    </Float>
  );
};

export default AIGlowingSphere2;
